import React from 'react';
import { Link, LinkProps } from '@mui/material';
import { Capacitor } from '@capacitor/core';
import { CapacitorBridge } from '@/services/CapacitorBridge';

interface ExternalLinkProps extends Omit<LinkProps, 'href' | 'onClick'> {
  url: string;
  children: React.ReactNode;
  disabled?: boolean;
}

const isNative = () =>
  Capacitor.getPlatform && Capacitor.getPlatform() !== 'web';

/**
 * Normalizes a URL by ensuring it has a protocol prefix.
 * URLs without http/https are treated as external and get https:// prepended.
 * Hash links (#) and protocol-relative URLs (//) are left unchanged.
 */
const normalizeUrl = (url: string): string => {
  if (!url) return '';
  // Already has protocol, hash link, or protocol-relative
  if (/^(https?:\/\/|#|\/\/)/.test(url)) {
    return url;
  }
  // Prepend https:// for URLs like www.example.com
  return `https://${url}`;
};

const openInCapacitor = async (url: string) => {
  const normalizedUrl = normalizeUrl(url);
  if (!/^https?:\/\//.test(normalizedUrl)) {
    console.error('Invalid external URL:', url);
    return;
  }
  try {
    await CapacitorBridge.execute('openUrl', { url: normalizedUrl });
  } catch (err) {
    console.error('Failed to open external link:', url, err);
  }
};

export function ExternalLink({
  url,
  disabled,
  children,
  ...linkProps
}: ExternalLinkProps) {
  const normalizedUrl = normalizeUrl(url);

  const handleNativeClick = (e: React.MouseEvent) => {
    if (disabled) return;
    e.preventDefault();
    openInCapacitor(normalizedUrl);
  };

  if (isNative()) {
    return (
      <Link
        {...linkProps}
        component='span'
        onClick={handleNativeClick}
        sx={{ cursor: disabled ? 'default' : 'pointer', ...linkProps.sx }}
      >
        {children}
      </Link>
    );
  }

  return (
    <Link
      {...linkProps}
      href={normalizedUrl}
      target='_blank'
      rel='noopener noreferrer'
    >
      {children}
    </Link>
  );
}
